import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Card, Table, TableHead, TableBody, TableRow, TableCell, Typography, IconButton, CircularProgress } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SearchableSelect from "components/SearchableSelect";
import SoftInput from "components/SoftInput";
import SoftSnackbar from "components/SoftSnackbar";
import SoftPagination from "components/SoftPagination";
import Icon from "@mui/material/Icon";
import { API_URL } from "config";
import { useAuth } from "../../AuthContext";
import FlagLoader from "layouts/flagloader";

const InnovateSessionsList = () => {
    /* ---------- PERMISSION SETUP ---------- */
    const { permissions: ctxPerms = [], isAuthReady } = useAuth();
    const storedPerms = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem("permissions") || "[]");
        } catch {
            return (localStorage.getItem("permissions") || "").split(",").filter(Boolean);
        }
    }, []);

    const permSet = useMemo(() => {
        const permissions = ctxPerms.length ? ctxPerms : storedPerms;
        return new Set(permissions.map(p => (typeof p === "string" ? p : p.name)));
    }, [ctxPerms, storedPerms]);

    const userHas = useCallback((colKey) => permSet.has(`viewstudies.${colKey}`), [permSet]);
    const userCanSearch = useCallback((fieldKey) => permSet.has(`searchstudies.${fieldKey}`), [permSet]);
    const userCanExport = useCallback(() => permSet.has("exportstudies"), [permSet]);

    /* ---------- STATE VARIABLES ---------- */
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pageLoading, setPageLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalSessions, setTotalSessions] = useState(0);
    const [fromData, setFromData] = useState(0);
    const [toData, setToData] = useState(0);
    const [message, setMessage] = useState(null);
    const [show, setShow] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);

    // Filters
    const [searchQuery, setSearchQuery] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);

    const token = localStorage.getItem("authToken");

    /* ---------- COLUMN DEFINITIONS (only required fields) ---------- */
    const columnDefs = [
        { key: "client_survey_id", label: "Survey ID" },
        { key: "qsid", label: "QSID" },
        { key: "ip", label: "IP Address" },
        { key: "cpi", label: "CPI" },
        { key: "status", label: "Status" },
        { key: "termReason", label: "Term Reason" },
        { key: "clickDateTime", label: "Click Date Time" },
        { key: "completeDateTime", label: "Complete Date Time" },
        { key: "created_at", label: "Created Time" },
    ];

    const visibleColumns = columnDefs;

    /* ---------- HANDLERS ---------- */
    const handleInputChange = (event) => {
        const value = event.target.value;
        if (value === "") {
            setCurrentPage("");
            return;
        }
        const pageNumber = Number(value);
        if (!isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(prev => prev + 1);
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) setCurrentPage(prev => prev - 1);
    };

    const toggleSnackbar = () => setShow(!show);

    const handleStatusChange = (selected) => {
        setCurrentPage(1);
        const values = selected ? selected.map(option => option.value) : [];
        setSelectedStatuses(values);
    };

    /* ---------- API CALLS ---------- */
    const fetchSessions = async () => {
        setLoading(true);
        try {
            let queryString = `per_page=20&page=${currentPage}`;

            if (searchQuery) queryString += `&q=${encodeURIComponent(searchQuery)}`;
            if (fromDate) queryString += `&from_date=${fromDate}`;
            if (toDate) queryString += `&to_date=${toDate}`;
            if (selectedStatuses.length > 0) {
                selectedStatuses.forEach(s => {
                    queryString += `&status[]=${encodeURIComponent(s)}`;
                });
            }

            const response = await fetch(`${API_URL}/innovate-sessions?${queryString}`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            const data = await response.json();

            // ✅ API response structure: data.current_page, data.data (array), data.last_page, etc.
            setSessions(data.data);                     // direct array
            setTotalPages(data.last_page);
            setFromData(data.from);
            setToData(data.to);
            setTotalSessions(data.total);

            // Build status filter options from unique status values in the current data (or from a separate list if API provides)
            const uniqueStatuses = [...new Set(data.data.map(item => item.status))];
            const options = uniqueStatuses.map(s => ({
                value: s,
                label: s,
            }));
            setStatusOptions(options);

        } catch (err) {
            setMessage(err.message);
            setShow(true);
        } finally {
            setLoading(false);
            setPageLoading(false);
        }
    };

    const exportData = async () => {
        setExportLoading(true);
        try {
            let queryParams = [];

            if (searchQuery) queryParams.push(`q=${encodeURIComponent(searchQuery)}`);
            if (fromDate) queryParams.push(`from_date=${fromDate}`);
            if (toDate) queryParams.push(`to_date=${toDate}`);
            if (selectedStatuses.length > 0) {
                selectedStatuses.forEach(s => queryParams.push(`status[]=${encodeURIComponent(s)}`));
            }

            const queryString = queryParams.length ? `?${queryParams.join("&")}` : "";
            const response = await fetch(`${API_URL}/innovate-sessions/export${queryString}`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            if (!response.ok) throw new Error(`Export failed: ${response.status}`);

            const blob = await response.blob();
            const now = new Date();
            const formattedDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}_${String(now.getHours()).padStart(2, "0")}-${String(now.getMinutes()).padStart(2, "0")}-${String(now.getSeconds()).padStart(2, "0")}`;
            const filename = `InnovateSessions_${formattedDate}.csv`;

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setMessage(err.message);
            setShow(true);
        } finally {
            setExportLoading(false);
        }
    };

    const handleDownload = () => exportData();

    /* ---------- EFFECTS ---------- */
    useEffect(() => {
        fetchSessions();
    }, [currentPage, searchQuery, fromDate, toDate, selectedStatuses]);

    useEffect(() => {
        setPageLoading(false);
    }, []);

    /* ---------- RENDER ---------- */
    if (pageLoading || !isAuthReady) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <SoftBox display="flex" justifyContent="center" alignItems="center" height="100vh">
                    <FlagLoader />
                </SoftBox>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <SoftSnackbar
                color="primary"
                icon="notifications"
                title="Notification"
                content={message ?? ""}
                open={show}
                close={toggleSnackbar}
            />

            <div style={{ display: "flex", gap: "10px" }}>
                {/* Main Table Card */}
                <Card style={{ flex: 1, borderRadius: "0px", padding: "10px" }}>
                    <div style={{ overflowX: "auto" }}>
                        {loading ? (
                            <FlagLoader />
                        ) : (
                            <>
                                {/* Pagination Header */}
                                <SoftBox display="flex" justifyContent="space-between" alignItems="center" p={1} style={{ width: "100%", fontSize: "13px" }}>
                                    <SoftBox fontWeight="small" style={{ width: "100%" }}>
                                        Showing {fromData} to {toData} of {totalSessions} entries
                                    </SoftBox>
                                    <SoftBox display="flex" justifyContent="end" alignItems="center" p={1} style={{ width: "100%" }}>
                                        <SoftPagination item onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                                            <Icon sx={{ fontWeight: "bold" }}>first_page</Icon>
                                        </SoftPagination>
                                        <SoftPagination item onClick={handlePreviousPage} disabled={currentPage === 1}>
                                            <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                                        </SoftPagination>
                                        <span style={{ margin: "0 5px", width: "120px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                            <span width="5rem" mx={1} style={{ fontSize: "9px" }}>
                                                <SoftInput inputProps={{ type: "number", min: 1, max: totalPages }} value={currentPage} onChange={handleInputChange} />
                                            </span>
                                            /<span>{totalPages}</span>
                                        </span>
                                        <SoftPagination item onClick={handleNextPage} disabled={currentPage === totalPages}>
                                            <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                                        </SoftPagination>
                                        <SoftPagination item onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
                                            <Icon sx={{ fontWeight: "bold" }}>last_page</Icon>
                                        </SoftPagination>
                                    </SoftBox>
                                </SoftBox>

                                {/* Data Table */}
                                <Table style={{ width: "100%" }}>
                                    <TableHead style={{ padding: "0px", display: "contents" }}>
                                        <TableRow style={{ backgroundColor: "#5c91c5", color: "#fff", width: "100%" }}>
                                            {visibleColumns.map((column) => (
                                                <TableCell key={column.key} style={{ color: "#fff", fontSize: "8.5px", textAlign: "center" }}>
                                                    {column.label}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {sessions.map((session, idx) => (
                                            <TableRow key={idx}>
                                                {visibleColumns.map((column) => {
                                                    let content = "N/A";
                                                    switch (column.key) {
                                                        case "client_survey_id":
                                                            content = session.client_survey_id ?? "N/A";
                                                            break;
                                                        case "qsid":
                                                            content = session.qsid ?? "N/A";
                                                            break;
                                                        case "ip":
                                                            content = session.ip ?? "N/A";
                                                            break;
                                                        case "cpi":
                                                            // API mein field name "CPI" hai (uppercase)
                                                            content = session.CPI ?? session.cpi ?? "N/A";
                                                            break;
                                                        case "status":
                                                            content = session.status ?? "N/A";
                                                            break;
                                                        case "termReason":
                                                            content = session.termReason ?? "N/A";
                                                            break;
                                                        case "clickDateTime":
                                                            content = session.clkDateTime ? new Date(session.clkDateTime).toLocaleString() : "N/A";
                                                            break;
                                                        case "completeDateTime":
                                                            content = session.completeDateTime ? new Date(session.completeDateTime).toLocaleString() : "N/A";
                                                            break;
                                                        case "created_at":
                                                            content = session.created_at
                                                                ? new Date(session.created_at).toLocaleString()
                                                                : "N/A";
                                                            break;
                                                        default:
                                                            content = "N/A";
                                                    }
                                                    return (
                                                        <TableCell key={column.key} style={{ fontSize: "9px", textAlign: "center" }}>
                                                            {content}
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </>
                        )}
                    </div>
                </Card>

                {/* Sidebar Filters Card */}
                <div style={{ display: "flex", flexDirection: "column", gap: "50px" }}>
                    <Card style={{ padding: "10px", width: "220px", borderRadius: "0px" }}>
                        {/* Total count + Export */}
                        <div>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <Typography variant="p" style={{ fontSize: "12px" }}>Total Sessions</Typography>
                                {userCanExport() && (
                                    <IconButton color="primary" onClick={handleDownload} disabled={exportLoading}>
                                        {exportLoading ? <CircularProgress size={24} /> : <DownloadIcon />}
                                    </IconButton>
                                )}
                            </div>
                            <Typography variant="h4" color="primary" style={{ fontSize: "20px", textAlign: "center" }}>
                                {totalSessions}
                            </Typography>
                        </div>

                        {/* Date Range Filters */}
                        {userCanSearch("date_range") && (
                            <>
                                <div style={{ marginTop: "12px" }}>
                                    <Typography variant="p" style={{ fontSize: "12px" }}>Start Date</Typography>
                                    <SoftInput type="datetime-local" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                                </div>
                                <div style={{ marginTop: "12px" }}>
                                    <Typography variant="p" style={{ fontSize: "12px" }}>End Date</Typography>
                                    <SoftInput type="datetime-local" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                                </div>
                            </>
                        )}

                        {/* General Search */}
                        {userCanSearch("general_search") && (
                            <div style={{ marginTop: "12px" }}>
                                <Typography variant="p" style={{ fontSize: "12px" }}>Search (any field)</Typography>
                                <SoftInput type="text" value={searchQuery} placeholder="Type here..." onChange={(e) => setSearchQuery(e.target.value)} />
                            </div>
                        )}

                        {/* Status Filter */}
                        {userCanSearch("status") && statusOptions.length > 0 && (
                            <div style={{ marginTop: "12px", marginBottom: "20px" }}>
                                <Typography variant="p" style={{ fontSize: "12px" }}>Status</Typography>
                                <SearchableSelect
                                    className="basic-multi-select"
                                    classNamePrefix="select"
                                    options={statusOptions}
                                    isMulti={true}
                                    fontSize={"10px"}
                                    onChange={handleStatusChange}
                                    placeholder="Filter by status..."
                                />
                            </div>
                        )}
                    </Card>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default InnovateSessionsList;
